import { Combobox, ComboboxButton, ComboboxInput } from "@headlessui/react";
import clsx from "clsx";
import { useTranslation } from "react-i18next";
import { HiChevronDown } from "react-icons/hi2";

import { ComboBoxBaseProps, SelectMultipleBaseProp, SelectOption } from "@/types/SelectTypes.type";
import Input from "@/components/atoms/input/Input";
import ComboBoxOptions from "@/components/molecules/combobox/ComboBoxOptions";
import SelectedOptions from "@/components/molecules/SelectedOptions";

export type ComboBoxMultipleProp<T extends object = {}> = SelectMultipleBaseProp<SelectOption<T>> & ComboBoxBaseProps;

const ComboBoxMultiple = <T extends { id: string; name: string }>({
  id,
  query = "",
  setQuery,
  options,
  value,
  onSelect,
  displayValue = (option) => option?.name,
  placeholder,
  showDropdownIcon = false,
  renderOption,
  renderChip,
  hasError,
  leftPlaceholder,
  optionsClassName,
  disabled,
  isLoading = false,
  optionsSearchable = false,
  emptyNode,
  allowFreeText = true,
  showOptionsOnFocus = false,
  renderBetween,
  chipClassName,
}: ComboBoxMultipleProp<T>) => {
  const { t } = useTranslation();

  const displayedPlaceholder = placeholder || t("select.options");

  function filterOptions<Extra extends object = {}>(
    opts: SelectOption<Extra>[],
    query: string,
    display: (o: SelectOption<Extra>) => string,
  ): SelectOption<Extra>[] {
    if (!query) return opts; // No search – return original array

    const q = query.toLowerCase();

    return opts
      .map((o) => {
        // Recursively filter children first
        const kids = o.children ? filterOptions(o.children, q, display) : [];

        // Does this option itself match?
        const selfMatches = display(o).toLowerCase().includes(q);

        // Keep the node if it (or any descendant) matches
        return selfMatches || kids.length ? { ...o, children: kids } : null;
      })
      .filter(Boolean) as SelectOption<Extra>[]; // drop the nulls
  }

  const filteredOptions = optionsSearchable ? filterOptions(options, query, displayValue) : options;

  const handleRemoveSelectedItem = (item: T) => {
    if (Array.isArray(value)) {
      const updatedValue = value.filter((v) => v.id !== item.id);
      onSelect(updatedValue);
    }
  };

  return (
    <div>
      <Combobox
        name={id}
        multiple={true}
        value={value}
        onChange={onSelect}
        disabled={disabled}
        onClose={() => setQuery("")}
        immediate={showOptionsOnFocus}
      >
        <div className={"relative"}>
          <ComboboxInput
            as={Input}
            hasError={hasError}
            leftPlaceholder={leftPlaceholder}
            className={clsx(showDropdownIcon && "pr-10")}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={displayedPlaceholder}
          />
          {showDropdownIcon && (
            <ComboboxButton disabled={disabled} className="absolute inset-y-0 right-0 flex items-center pr-3.5">
              <HiChevronDown className="size-5 text-fg-secondary" />
            </ComboboxButton>
          )}

          <ComboBoxOptions
            allowFreeText={allowFreeText}
            query={query}
            filteredOptions={filteredOptions}
            isLoading={isLoading}
            renderOption={renderOption}
            displayValue={displayValue}
            optionsClassName={optionsClassName}
            emptyNode={emptyNode}
            showOptionsOnFocus={showOptionsOnFocus}
          />
        </div>
      </Combobox>

      {renderBetween}

      <SelectedOptions
        options={options}
        value={value}
        displayValue={displayValue}
        renderChip={renderChip}
        chipClassName={chipClassName}
        handleRemoveSelectedItem={handleRemoveSelectedItem}
      />
    </div>
  );
};

export default ComboBoxMultiple;
